/**
 * Dialog para exibir detalhes de erro de envio de mensagem
 * 
 * Consulta catálogo dinâmico do banco via useErrorCatalog.
 * Mantém fallback para tradução de títulos da API Meta.
 * 
 * @release v2.0.0 - 2026-02-28 - Migrado para catálogo dinâmico
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  AlertCircle, 
  ChevronDown, 
  Lightbulb, 
  FileWarning,
  Clock,
  PhoneOff,
  Server,
  Ban,
  ImageOff,
  FileX,
  CreditCard,
  ShieldAlert,
  HelpCircle,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useErrorCatalog, type ErrorInfo } from '@/hooks/useErrorCatalog';
import { extractEmbeddedErrorCode } from '@/lib/errorMetadata';

// Interface para dados de erro recebidos do metadata da mensagem
interface ErrorData {
  code?: number | string;
  title?: string;
  details?: string;
  message?: string;
  occurred_at?: string;
  [key: string]: unknown;
}

interface ErrorDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  error: ErrorData | null;
  messageType?: string;
  sentAt?: string;
  /** Slug do provider da inbox (ex: 'meta-whatsapp') para match obrigatório no catálogo */
  providerSlug?: string;
}

/**
 * Mapa de traduções para títulos comuns da API Meta
 * Usado quando a API envia título em inglês
 */
const apiTitleTranslations: Record<string, string> = {
  'Business eligibility payment issue': 'Problema de Pagamento da Conta',
  'Message failed to send': 'Falha no Envio da Mensagem',
  'User is not eligible': 'Usuário Não Elegível',
  'Re-engagement message': 'Mensagem de Reengajamento',
  'Rate limit hit': 'Limite de Taxa Atingido',
  'Spam rate limit hit': 'Limite de Spam Atingido',
  'Too many messages': 'Muitas Mensagens Enviadas',
  'Phone number not valid': 'Número de Telefone Inválido',
  'Recipient not valid': 'Destinatário Inválido',
  'Template not found': 'Template Não Encontrado',
  'Template paused': 'Template Pausado',
  'Account suspended': 'Conta Suspensa',
  'Business account locked': 'Conta Comercial Bloqueada',
};

/**
 * Traduz título da API para português
 */
const translateApiTitle = (title: string): string => {
  if (apiTitleTranslations[title]) return apiTitleTranslations[title];
  for (const [key, translation] of Object.entries(apiTitleTranslations)) {
    if (title.toLowerCase().includes(key.toLowerCase())) return translation;
  }
  return title;
};

/**
 * Mapa de ícones por nome (correspondente ao icon_name do catálogo)
 */
const iconMap: Record<string, (color: string) => React.ReactNode> = {
  'file-warning': (color) => <FileWarning className={`h-5 w-5 text-${color}`} />,
  'image-off': (color) => <ImageOff className={`h-5 w-5 text-${color}`} />,
  'file-x': (color) => <FileX className={`h-5 w-5 text-${color}`} />,
  'clock': (color) => <Clock className={`h-5 w-5 text-${color}`} />,
  'ban': (color) => <Ban className={`h-5 w-5 text-${color}`} />,
  'phone-off': (color) => <PhoneOff className={`h-5 w-5 text-${color}`} />,
  'shield-alert': (color) => <ShieldAlert className={`h-5 w-5 text-${color}`} />,
  'credit-card': (color) => <CreditCard className={`h-5 w-5 text-${color}`} />,
  'alert-circle': (color) => <AlertCircle className={`h-5 w-5 text-${color}`} />,
  'server': (color) => <Server className={`h-5 w-5 text-${color}`} />,
  'help-circle': (color) => <HelpCircle className={`h-5 w-5 text-muted-foreground`} />,
};

/**
 * Retorna ícone React baseado no icon_name e icon_color do catálogo
 */
const getIconFromCatalog = (iconName: string | null, iconColor: string | null): React.ReactNode => {
  const name = iconName || 'help-circle';
  const color = iconColor || 'muted-foreground';
  const iconFn = iconMap[name];
  if (iconFn) return iconFn(color);
  return <HelpCircle className="h-5 w-5 text-muted-foreground" />;
};

/**
 * Retorna ícone fallback baseado no código de erro (quando catálogo não tem dados)
 */
const getIconByCode = (code: number | string | undefined): React.ReactNode => {
  const codeStr = String(code);
  if (codeStr.startsWith('1310') && parseInt(codeStr) >= 131051 && parseInt(codeStr) <= 131099) {
    return <FileWarning className="h-5 w-5 text-amber-500" />;
  }
  if (['131049'].includes(codeStr)) return <Ban className="h-5 w-5 text-amber-500" />;
  if (['131047', '131026'].includes(codeStr)) return <Clock className="h-5 w-5 text-blue-500" />;
  if (['130472', '131031'].includes(codeStr)) return <PhoneOff className="h-5 w-5 text-red-500" />;
  if (['131056', '80007'].includes(codeStr)) return <AlertCircle className="h-5 w-5 text-orange-500" />;
  if (['500', '503', '131000'].includes(codeStr)) return <Server className="h-5 w-5 text-red-500" />;
  return <HelpCircle className="h-5 w-5 text-muted-foreground" />;
};

/**
 * Retorna categoria fallback baseada no código de erro
 */
const getCategoryByCode = (code: number | string | undefined): string => {
  const codeStr = String(code);
  if (codeStr.startsWith('1310') && parseInt(codeStr) >= 131051 && parseInt(codeStr) <= 131099) return 'Arquivo';
  if (['131049'].includes(codeStr)) return 'Engajamento';
  if (['131047', '131026'].includes(codeStr)) return 'Janela de Conversa';
  if (['130472', '131031', '131042'].includes(codeStr)) return 'Contato/Conta';
  if (['131056', '80007'].includes(codeStr)) return 'Limite';
  if (['500', '503', '131000'].includes(codeStr)) return 'Sistema';
  return 'Geral';
};

export function ErrorDetailsDialog({ 
  open, 
  onClose, 
  error, 
  messageType, 
  sentAt,
  providerSlug,
}: ErrorDetailsDialogProps) {
  const [showTechnical, setShowTechnical] = useState(false);
  
  // Busca catálogo dinâmico do banco de dados
  const { getErrorInfo } = useErrorCatalog();
  
  // Códigos genéricos que devem tentar extração inteligente
  const GENERIC_CODES = ['500', '503', 'UNKNOWN'];

  // IMPORTANTE: useMemo DEVE ser chamado ANTES de qualquer early return
  // para respeitar as regras de hooks do React (ordem constante de chamada)
  const catalogInfo: ErrorInfo | null = useMemo(() => {
    if (!error) return null;

    // Passo 1: busca pelo código original com provider obrigatório
    const directMatch = getErrorInfo(
      error.code,
      providerSlug,
      error.details || error.message,
      error.title
    );
    
    // Passo 2: se código é genérico, tenta extrair código real do texto
    if ((!directMatch || GENERIC_CODES.includes(String(error.code))) ) {
      const combinedText = `${error.details || ''} ${error.message || ''} ${error.title || ''}`;
      const extractedCode = extractEmbeddedErrorCode(combinedText);
      
      if (extractedCode && extractedCode !== String(error.code)) {
        // Busca refinada também com provider obrigatório
        const refinedMatch = getErrorInfo(extractedCode, providerSlug, error.details || error.message, error.title);
        if (refinedMatch) return refinedMatch;
      }
    }
    
    return directMatch;
  }, [error, getErrorInfo, providerSlug]);

  // Early return DEPOIS de todos os hooks para não violar as regras do React
  if (!error) return null;

  // Mensagem técnica original (para análise e fallback)
  const technicalMessage = error.details || error.message || error.title || 'Erro desconhecido';
  
  // Se catálogo retornou dados, usa com ícone dinâmico
  const catalogDisplay = catalogInfo ? {
    title: catalogInfo.title,
    explanation: catalogInfo.explanation,
    suggestion: catalogInfo.suggestion,
    icon: getIconFromCatalog(catalogInfo.icon_name, catalogInfo.icon_color),
    category: catalogInfo.category,
    is_cataloged: catalogInfo.is_cataloged,
  } : null;
  
  // Se não houver mapeamento no catálogo, mas API enviou dados, criar explicação dinâmica
  const apiProvidedInfo = !catalogDisplay && error.title ? {
    title: translateApiTitle(error.title),
    explanation: error.details || error.message || error.title,
    icon: getIconByCode(error.code),
    category: getCategoryByCode(error.code),
    suggestion: 'Verifique os detalhes técnicos abaixo e contate o suporte se necessário.',
    is_cataloged: true,
  } : null;
  
  // Usa catálogo dinâmico > API > fallback
  const displayInfo = catalogDisplay || apiProvidedInfo;
  
  // Formata data/hora da ocorrência
  const formattedTime = sentAt 
    ? format(new Date(sentAt), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })
    : error.occurred_at 
      ? format(new Date(error.occurred_at), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })
      : null;
  
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            Falha no Envio
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Badge para erros não catalogados */}
          {displayInfo && !displayInfo.is_cataloged && (
            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
              Erro novo — ainda sem solução catalogada
            </Badge>
          )}
          
          {/* Seção principal - Título e explicação amigável */}
          <div className="flex gap-3 p-3 bg-muted/50 rounded-lg">
            {displayInfo ? (
              <>
                <div className="flex-shrink-0 mt-0.5">
                  {displayInfo.icon}
                </div>
                <div className="space-y-1">
                  <h4 className="font-medium text-sm">
                    {displayInfo.title}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {displayInfo.explanation}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="flex-shrink-0 mt-0.5">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                </div>
                <div className="space-y-1">
                  <h4 className="font-medium text-sm">
                    {error.title || 'Erro ao Enviar Mensagem'}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {technicalMessage}
                  </p>
                </div>
              </>
            )}
          </div>
          
          {/* Sugestão de resolução */}
          {displayInfo && (
            <div className="flex gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <Lightbulb className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h5 className="text-xs font-medium text-primary mb-1">Sugestão</h5>
                <p className="text-sm text-muted-foreground">
                  {displayInfo.suggestion}
                </p>
              </div>
            </div>
          )}
          
          {/* Detalhes técnicos (colapsável) */}
          <Collapsible open={showTechnical} onOpenChange={setShowTechnical}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full justify-between text-xs text-muted-foreground hover:text-foreground"
              >
                Detalhes Técnicos (para suporte)
                <ChevronDown className={`h-4 w-4 transition-transform ${showTechnical ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 p-3 bg-muted rounded-lg space-y-2 text-xs font-mono">
                {error.code && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Código:</span>
                    <span className="font-semibold">{error.code}</span>
                  </div>
                )}
                {messageType && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tipo:</span>
                    <span>{messageType}</span>
                  </div>
                )}
                {formattedTime && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ocorreu em:</span>
                    <span>{formattedTime}</span>
                  </div>
                )}
                {displayInfo && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Categoria:</span>
                    <span>{displayInfo.category}</span>
                  </div>
                )}
                {/* Mensagem original do erro */}
                {/* Container com scroll para mensagens longas */}
                <div className="pt-2 border-t border-border">
                  <span className="text-muted-foreground block mb-1">Mensagem original:</span>
                  <div className="max-h-[120px] overflow-y-auto rounded border border-border/50 p-2">
                    <p className="text-xs break-all whitespace-pre-wrap">
                      {technicalMessage}
                    </p>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
        
        {/* Botão de fechar */}
        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
